import React, { useState, useEffect, useRef } from "react";
import {
  Typography,
  Row,
  Col,
  Button,
  Modal,
  Tag,
  Input,
  Select,
  message,
  Tooltip,
} from "antd";
import {
  MonitorPlay,
  Smartphone,
  Link,
  LayoutTemplate,
  Crown,
  Clock,
  Wifi,
  Info,
  RefreshCw,
  Send,
  Eye,
  Monitor,
  Globe,
  Unplug,
  Save,
} from "lucide-react";
import { motion } from "framer-motion";
import PreviewPlayer from "../components/PreviewPlayer";
import LiteAPI from "../api/liteAPI";
import { useLanguage, UI_TRANSLATIONS } from "../LanguageContext";

const { Title, Text } = Typography;
const { Option } = Select;

const TIMEZONE_OPTIONS = [
  { value: "Pacific/Honolulu", label: "(GMT-10:00) Hawaii" },
  { value: "America/Anchorage", label: "(GMT-09:00) Alaska" },
  { value: "America/Los_Angeles", label: "(GMT-08:00) Pacific Time" },
  { value: "America/Denver", label: "(GMT-07:00) Mountain Time" },
  { value: "America/Chicago", label: "(GMT-06:00) Central Time" },
  { value: "America/New_York", label: "(GMT-05:00) Eastern Time" },
  { value: "America/Sao_Paulo", label: "(GMT-03:00) São Paulo" },
  { value: "Europe/London", label: "(GMT+00:00) London" },
  { value: "Europe/Paris", label: "(GMT+01:00) Paris / Berlin" },
  { value: "Europe/Istanbul", label: "(GMT+03:00) Istanbul" },
  { value: "Asia/Dubai", label: "(GMT+04:00) Dubai" },
  { value: "Asia/Kolkata", label: "(GMT+05:30) India" },
  { value: "Asia/Bangkok", label: "(GMT+07:00) Bangkok" },
  { value: "Asia/Singapore", label: "(GMT+08:00) Singapore" },
  { value: "Asia/Tokyo", label: "(GMT+09:00) Tokyo" },
  { value: "Australia/Sydney", label: "(GMT+11:00) Sydney" },
  { value: "Pacific/Auckland", label: "(GMT+12:00) Auckland" },
];

const stagger = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  show: {
    opacity: 1,
    y: 0,
    transition: { type: "spring", stiffness: 300, damping: 24 },
  },
};

const ScreenManager = ({
  hasScreen,
  setHasScreen,
  screenData,
  setScreenData,
  allScreens = [],
  selectedRatio,
  setSelectedRatio,
  ratios,
  screenPlaylists = {},
  playlistItems = [],
  setPlaylistItems,
  onUnpaired,
  onNavigate,
  apiProps,
  brandData,
  branchData,
  refreshScreens,
  onScreenPaired,
  onProUpgrade,
}) => {
  const [isPairing, setIsPairing] = useState(false);
  const [pairCode, setPairCode] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [isUnpairing, setIsUnpairing] = useState(false);
  // Index of the screen card being viewed (0 = Screen 1, 1 = Screen 2)
  const [viewedScreenIdx, setViewedScreenIdx] = useState(0);
  const [resolutionMode, setResolutionMode] = useState("preset"); // "preset" | "custom"
  const previewContainerRef = useRef(null);
  const [previewMaxW, setPreviewMaxW] = useState(380);

  // Keep the preview frame's max width in sync with the actual container
  // width so aspect ratios stay visually distinct on narrow (mobile/tablet)
  // screens instead of both getting clamped down toward square.
  useEffect(() => {
    const el = previewContainerRef.current;
    if (!el) return;
    const containerPadding = 48; // 24px left + 24px right
    const updateWidth = () => {
      setPreviewMaxW(Math.max(120, el.clientWidth - containerPadding));
    };
    updateWidth();
    const ro = new ResizeObserver(updateWidth);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [customWidths, setCustomWidths] = useState([
    screenData?.ScreenWidth || 1920,
    1920,
  ]);
  const [customHeights, setCustomHeights] = useState([
    screenData?.ScreenHeight || 1080,
    1080,
  ]);

  console.log("allScreens:", allScreens);
  const customWidth = customWidths[viewedScreenIdx] ?? 1920;
  const customHeight = customHeights[viewedScreenIdx] ?? 1080;
  const setCustomWidth = (val) =>
    setCustomWidths((prev) => {
      const a = [...prev];
      a[viewedScreenIdx] = val;
      return a;
    });
  const setCustomHeight = (val) =>
    setCustomHeights((prev) => {
      const a = [...prev];
      a[viewedScreenIdx] = val;
      return a;
    });
  const [isSavingResolution, setIsSavingResolution] = useState(false);
  // Live device info from Firebase (os, os_version, player_version, app_version, device_model)
  const [fbStatus, setFbStatus] = useState({});
  const inputRef = useRef(null);
  const { lang } = useLanguage();
  const t = UI_TRANSLATIONS[lang].screen;

  // Derive which screen card is being viewed
  const viewedScreen =
    allScreens.length > 0
      ? (allScreens[viewedScreenIdx] ?? allScreens[0])
      : screenData;
  // Items for the viewed screen's preview
  const viewedPlaylistItems =
    viewedScreen && screenPlaylists[viewedScreen.id]
      ? screenPlaylists[viewedScreen.id].items
      : playlistItems;

  // Sync customWidths/customHeights from allScreens whenever screen data refreshes
  useEffect(() => {
    if (allScreens.length === 0) return;
    setCustomWidths((prev) => {
      const next = [...prev];
      allScreens.slice(0, 2).forEach((s, i) => {
        const w = s?.ScreenWidth || s?.raw?.ScreenWidth;
        if (w) next[i] = w;
      });
      return next;
    });
    setCustomHeights((prev) => {
      const next = [...prev];
      allScreens.slice(0, 2).forEach((s, i) => {
        const h = s?.ScreenHeight || s?.raw?.ScreenHeight;
        if (h) next[i] = h;
      });
      return next;
    });
  }, [allScreens]);

  useEffect(() => {
    if (isPairing) {
      setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 100);
    }
  }, [isPairing]);

  // Subscribe to Firebase real-time device status for the viewed screen
  useEffect(() => {
    if (!viewedScreen?.raw) return;
    setFbStatus({});
    const unsubscribe = LiteAPI.subscribeScreenStatus(
      viewedScreen.raw,
      (data) => {
        console.log("Firebase status update:", data);
        setFbStatus(data);
      },
    );
    return unsubscribe;
  }, [viewedScreen?.raw?.ScreenCode]);

  const handlePair = async () => {
    if (pairCode.length !== 6) {
      message.error(t.pairCodeError);
      return;
    }
    if (!apiProps || !brandData) {
      message.error(t.pairMissingData);
      return;
    }
    try {
      const result = await LiteAPI.pairScreen(
        pairCode,
        brandData.BrandID,
        branchData?.BranchID || brandData.BrandID,
        apiProps,
      );
      // Normalise response shape (API may return flat or nested screen object)
      const rawScreen = result?.AccountCode
        ? result
        : result?.screen || result?.data || result;
      // CampaignID from create_screen_lite — Warroom's get_screens doesn't return it
      const pairedCampaignId = rawScreen?.CampaignID;
      // Clear any pending unpair request on the device via Firebase
      if (rawScreen?.AccountCode) {
        LiteAPI.clearUnpairRequest(rawScreen);
      }
      // Auto-name the screen as MyScreen-[ScreenCode]
      const screenCode = rawScreen?.ScreenCode;
      const screenId = rawScreen?.ScreenID;
      // if (screenId && screenCode) {
      //   try {
      //     await LiteAPI.updateScreenName(
      //       screenId,
      //       `MyScreen-${screenCode}`,
      //       brandData.BrandID,
      //       branchData?.BranchID || brandData.BrandID,
      //       apiProps,
      //     );
      //   } catch (_) {
      //     // non-critical, continue
      //   }
      // }
      setIsPairing(false);
      setPairCode("");
      Modal.success({
        title: t.pairSuccessTitle,
        content: t.pairSuccessContent,
      });
      if (refreshScreens) await refreshScreens();
      if (onScreenPaired) await onScreenPaired(rawScreen);
      // Patch CampaignID into screenData — Warroom's get_screens omits it
      if (pairedCampaignId) {
        setScreenData((prev) =>
          prev
            ? {
                ...prev,
                raw: { ...(prev.raw || {}), CampaignID: pairedCampaignId },
              }
            : prev,
        );
      }
    } catch (err) {
      message.error(err.message || t.pairFail);
      console.error("Pairing error:", err);
    }
  };

  const handleTimezoneChange = async (value) => {
    setScreenData((prev) => ({ ...prev, Timezone: value }));
    if (viewedScreen?.raw?.AccountCode) {
      try {
        await LiteAPI.updateScreenTimezone(viewedScreen.raw, value);
        message.success(t.timezoneUpdated(value));
      } catch (_) {
        message.warning(t.timezoneLocal);
      }
    } else {
      message.success(t.timezoneUpdated(value));
    }
  };

  const handlePublish = async () => {
    if (viewedPlaylistItems.length === 0) {
      message.warning(t.publishAddFirst);
      return;
    }
    if (!apiProps || !viewedScreen?.raw) {
      message.warning(t.publishNoScreen);
      return;
    }
    if (!viewedScreen.raw?.CampaignID) {
      message.warning(t.publishNoCampaign);
      return;
    }
    try {
      await LiteAPI.publishContent([viewedScreen.raw], apiProps);
      message.success(
        t.publishSuccess(
          viewedPlaylistItems.length,
          viewedScreen?.name || viewedScreen?.ScreenName || "screen",
        ),
      );
    } catch (err) {
      message.error(t.publishFail);
      console.error("Publish error:", err);
    }
  };

  const handleUnpair = () => {
    Modal.confirm({
      title: t.unpairTitle,
      icon: <Unplug size={20} color="#ef4444" />,
      content: t.unpairContent,
      okText: t.unpairOk,
      okButtonProps: { danger: true },
      cancelText: t.unpairCancel,
      onOk: async () => {
        if (!viewedScreen?.raw || !apiProps) {
          message.error(t.publishNoScreen);
          return;
        }
        const unpairedId = viewedScreen.raw.ScreenID;
        setIsUnpairing(true);
        try {
          await LiteAPI.unpairScreen(viewedScreen.raw, apiProps);
          message.success(t.unpairSuccess);
          // If unpairing Screen 1 (idx 0), shift view to Screen 2 or reset
          if (viewedScreenIdx > 0) setViewedScreenIdx(0);
          if (allScreens.length <= 1) {
            setHasScreen(false);
            setScreenData(null);
          }
          if (setPlaylistItems) setPlaylistItems([]);
          if (onUnpaired) onUnpaired(unpairedId);
          if (refreshScreens) await refreshScreens();
        } catch (err) {
          message.error(t.unpairFail);
          console.error("Unpair error:", err);
        } finally {
          setIsUnpairing(false);
        }
      },
    });
  };

  const handleSaveResolution = async () => {
    if (!viewedScreen?.raw || !apiProps || !brandData) {
      message.warning(t.publishNoScreen);
      return;
    }
    setIsSavingResolution(true);
    try {
      await LiteAPI.updateScreenResolution(
        viewedScreen.raw.ScreenID,
        customWidth,
        customHeight,
        brandData.BrandID,
        branchData?.BranchID || brandData.BrandID,
        apiProps,
        viewedScreen.raw.CampaignID,
      );
      message.success(`Saved ${customWidth} × ${customHeight} px`);
      if (refreshScreens) await refreshScreens();
    } catch (err) {
      message.error("Failed to save resolution.");
      console.error("Save resolution error:", err);
    } finally {
      setIsSavingResolution(false);
    }
  };

  const handleRefresh = async () => {
    if (refreshScreens) {
      await refreshScreens();
      message.success(t.refreshSuccess);
    } else {
      message.info(t.refreshSuccess);
    }
  };

  const handleProZones = () => {
    Modal.info({
      icon: <Crown color="#f59e0b" size={24} />,
      title: t.proZonesTitle,
      content: t.proZonesContent,
      okText: "Got it",
    });
  };

  // Calculate preview dimensions
  const getPreviewDimensions = (ratio, customW, customH) => {
    const maxW = Math.min(380, previewMaxW);
    const maxH = 280;
    let aspect;
    if (ratio === "custom") {
      aspect = (customW || 1920) / (customH || 1080);
    } else {
      const [w, h] = ratio.value.split("/").map(Number);
      aspect = w / h;
    }

    // Fit the aspect ratio inside maxW x maxH, scaling both dimensions together
    let width = maxW;
    let height = maxW / aspect;
    if (height > maxH) {
      height = maxH;
      width = maxH * aspect;
    }
    return { width: Math.round(width), height: Math.round(height) };
  };

  const previewDims =
    resolutionMode === "custom"
      ? getPreviewDimensions("custom", customWidth, customHeight)
      : getPreviewDimensions(selectedRatio);

  return (
    <motion.div variants={stagger} initial="hidden" animate="show">
      {/* ── Header ── */}
      <motion.div variants={fadeUp} className="lite-section-header">
        <div>
          <h2>{t.title}</h2>
          <p>{hasScreen ? t.activeSubtitle : t.inactiveSubtitle}</p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          {allScreens.length > 0 && (
            <Text
              strong
              style={{
                fontSize: "14px",
                color: "#0f172a",
                whiteSpace: "nowrap",
              }}
            >
              {allScreens.length >= 2
                ? `Screen ${allScreens.length} / 2 Max`
                : `Screen ${allScreens.length} / 2`}
            </Text>
          )}
          {allScreens.length >= 2 ? (
            <Button
              onClick={onProUpgrade || handleProZones}
              icon={<Crown size={15} color="#222d51" />}
              className="lite-upgrade-btn"
              style={{
                borderRadius: "8px",
                background: "#ffcf5d",
                borderColor: "#ffcf5d",
                color: "#222d51",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              Upgrade to{" "}
              <span
                style={{
                  background: "#0f172a",
                  color: "#fff",
                  borderRadius: "4px",
                  padding: "1px 6px",
                  fontSize: "11px",
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                }}
              >
                PRO
              </span>
            </Button>
          ) : (
            <Button
              type="primary"
              icon={<MonitorPlay size={16} />}
              onClick={() => setIsPairing(true)}
              style={{
                borderRadius: "8px",
                background: "#6366f1",
                borderColor: "#6366f1",
              }}
            >
              {allScreens.length === 1 ? t.addScreen2 : t.pairBtn}
            </Button>
          )}
        </div>
      </motion.div>

      {/* ── Screen Selector dropdown (always shown, even for a single screen) ── */}
      {allScreens.length >= 1 && (
        <motion.div variants={fadeUp} style={{ marginBottom: "24px" }}>
          <Select
            value={viewedScreenIdx}
            onChange={(val) => setViewedScreenIdx(val)}
            style={{ width: "100%" }}
            size="large"
            optionLabelProp="label"
          >
            {allScreens.slice(0, 2).map((screen, idx) => (
              <Select.Option
                key={screen.id}
                value={idx}
                label={
                  <span
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "8px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    <MonitorPlay size={14} color="#6366f1" />
                    <span
                      style={{ fontWeight: 600 }}
                    >{`Screen ${idx + 1}`}</span>
                  </span>
                }
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "4px 0",
                  }}
                >
                  <div
                    style={{
                      width: "32px",
                      height: "32px",
                      borderRadius: "8px",
                      background:
                        viewedScreenIdx === idx ? "#ede9fe" : "#f1f5f9",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <MonitorPlay
                      size={14}
                      color={viewedScreenIdx === idx ? "#6366f1" : "#94a3b8"}
                    />
                  </div>
                  <div>
                    <div
                      style={{
                        fontWeight: 600,
                        fontSize: "13px",
                        color: "#0f172a",
                      }}
                    >
                      {`Screen ${idx + 1}`}
                    </div>
                    <div style={{ fontSize: "11px", color: "#94a3b8" }}>
                      {screen.name || screen.ScreenName || "LiteScreen"}
                    </div>
                  </div>
                </div>
              </Select.Option>
            ))}
          </Select>
        </motion.div>
      )}

      {hasScreen ? (
        <Row gutter={[24, 24]}>
          {/* ── Left: Screen Info ── */}
          <Col xs={24} lg={14}>
            <motion.div
              variants={fadeUp}
              className="lite-card no-hover"
              style={{ padding: 0, overflow: "hidden" }}
            >
              {/* Screen header */}
              <div
                style={{
                  padding: "20px 24px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "14px" }}
                >
                  <div className="lite-stat-icon purple">
                    <MonitorPlay size={24} color="#6366f1" />
                  </div>
                  <div>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <Title level={4} style={{ margin: 0, fontSize: "16px" }}>
                        {viewedScreen?.name ||
                          viewedScreen?.ScreenName ||
                          "My Screen"}
                      </Title>
                      {/* {screenPlaylists[viewedScreen?.id]?.campaignName && (
                        <Tag
                          className="lite-tag-campaign"
                          style={{
                            margin: 0,
                            borderRadius: "8px",
                            fontSize: "11px",
                            padding: "1px 8px",
                          }}
                        >
                          {screenPlaylists[viewedScreen.id].campaignName}
                        </Tag>
                      )} */}
                    </div>
                    <Text type="secondary" style={{ fontSize: "13px" }}>
                      {t.freeTier}
                    </Text>
                  </div>
                </div>
                <Tag
                  color={fbStatus?.is_online === 1 ? "#10b981" : "default"}
                  className={fbStatus?.is_online === 1 ? "lite-tag-online" : ""}
                  style={{
                    margin: 0,
                    borderRadius: "12px",
                    fontSize: "12px",
                    padding: "3px 12px",
                  }}
                >
                  {fbStatus?.is_online === 1 ? t.onlineStatus : t.offlineStatus}
                </Tag>
              </div>

              {/* Info rows */}
              {[
                {
                  icon: <Wifi size={18} color="#10b981" />,
                  label: t.connection,
                  value: t.connectedStatus(
                    viewedScreen?.LastSeen || "recently",
                  ),
                },
                {
                  icon: <LayoutTemplate size={18} color="#eab308" />,
                  label: t.campaign || "Campaign",
                  value: screenPlaylists[viewedScreen?.id]?.campaignName || "-",
                  isYellow: true,
                },
                {
                  icon: <Monitor size={18} color="#8b5cf6" />,
                  label: t.device,
                  value:
                    fbStatus?.device_model || viewedScreen?.DeviceModel || "-",
                },
                {
                  icon: <Info size={18} color="#64748b" />,
                  label: t.os,
                  value: (() => {
                    const os = fbStatus?.os || viewedScreen?.OS || "";
                    const osVer =
                      fbStatus?.os_version || viewedScreen?.OSVersion || "";
                    const player =
                      fbStatus?.player_version ||
                      viewedScreen?.PlayerVersion ||
                      "";
                    return `${os || "-"} ${osVer} ${player ? `[${player}]` : ""}`.trim();
                  })(),
                },
                {
                  icon: <Info size={18} color="#64748b" />,
                  label: t.appVersion,
                  value:
                    fbStatus?.player_version ||
                    viewedScreen?.PlayerVersion ||
                    "-",
                },
              ].map((row, idx, arr) => (
                <div
                  key={idx}
                  style={{
                    padding: "16px 24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    borderBottom:
                      idx < arr.length - 1 ? "1px solid #f8fafc" : "none",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "12px",
                    }}
                  >
                    {row.icon}
                    <Text style={{ color: "#64748b", fontSize: "13px" }}>
                      {row.label}
                    </Text>
                  </div>
                  {row.isYellow && row.value !== "-" ? (
                    <Tag
                      className="lite-tag-campaign"
                      style={{
                        margin: 0,
                        borderRadius: "8px",
                        fontSize: "12px",
                        padding: "1px 10px",
                      }}
                    >
                      {row.value}
                    </Tag>
                  ) : (
                    <Text strong style={{ fontSize: "13px", color: "#0f172a" }}>
                      {row.value}
                    </Text>
                  )}
                </div>
              ))}

              {/* Timezone selector */}
              <div
                style={{
                  padding: "16px 24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  borderTop: "1px solid #f1f5f9",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "12px" }}
                >
                  <Globe size={18} color="#3b82f6" />
                  <Text style={{ color: "#64748b", fontSize: "13px" }}>
                    {t.timezone}
                  </Text>
                </div>
                <Select
                  value={viewedScreen?.Timezone || "UTC"}
                  onChange={handleTimezoneChange}
                  style={{ width: "220px" }}
                  size="small"
                  showSearch
                  filterOption={(input, option) =>
                    option.children.toLowerCase().includes(input.toLowerCase())
                  }
                >
                  {TIMEZONE_OPTIONS.map((tz) => (
                    <Option key={tz.value} value={tz.value}>
                      {tz.label}
                    </Option>
                  ))}
                </Select>
              </div>

              {/* Actions */}
              <div
                className="lite-screen-actions"
                style={{
                  padding: "16px 24px",
                  background: "#fafbfc",
                  display: "flex",
                  gap: "12px",
                }}
              >
                <Button
                  type="primary"
                  icon={<Send size={14} />}
                  onClick={handlePublish}
                  disabled={viewedPlaylistItems.length === 0}
                  className={
                    viewedPlaylistItems.length > 0 ? "lite-publish-btn" : ""
                  }
                  style={{
                    flex: 1,
                    borderRadius: "8px",
                    height: "40px",
                    fontWeight: 600,
                    background:
                      viewedPlaylistItems.length > 0 ? "#6366f1" : undefined,
                    borderColor:
                      viewedPlaylistItems.length > 0 ? "#6366f1" : undefined,
                  }}
                >
                  {t.publishBtn}
                </Button>
                <Button
                  icon={<RefreshCw size={14} />}
                  style={{ borderRadius: "8px", height: "40px" }}
                  onClick={handleRefresh}
                >
                  {t.refresh}
                </Button>
                <Button
                  danger
                  icon={<Unplug size={14} />}
                  style={{ borderRadius: "8px", height: "40px" }}
                  onClick={handleUnpair}
                  loading={isUnpairing}
                >
                  {t.unpair}
                </Button>
              </div>
            </motion.div>
          </Col>

          {/* ── Right: Layout Preview ── */}
          <Col xs={24} lg={10}>
            <motion.div variants={fadeUp} className="lite-card no-hover">
              {/* Ratio Selector */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <LayoutTemplate size={18} color="#64748b" />
                  <Text strong style={{ fontSize: "14px" }}>
                    {t.screenPreview}
                  </Text>
                </div>
                {/* <Tooltip title="Zone layouts available in Pro" placement="top">
                  <Button
                    size="small"
                    type="dashed"
                    icon={<Crown size={12} color="#f59e0b" />}
                    onClick={handleProZones}
                    style={{
                      borderRadius: "6px",
                      fontSize: "11px",
                      color: "#f59e0b",
                      borderColor: "#fde68a",
                    }}
                  >
                    {t.zones}
                  </Button>
                </Tooltip> */}
              </div>

              {/* Resolution Mode Tabs */}
              <div
                style={{
                  display: "flex",
                  gap: "4px",
                  marginBottom: "16px",
                  background: "#f1f5f9",
                  borderRadius: "10px",
                  padding: "4px",
                }}
              >
                {["preset", "custom"].map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setResolutionMode(mode)}
                    style={{
                      flex: 1,
                      padding: "6px 0",
                      borderRadius: "8px",
                      border: "none",
                      cursor: "pointer",
                      fontWeight: 600,
                      fontSize: "12px",
                      transition: "all 0.2s",
                      background:
                        resolutionMode === mode ? "#ffffff" : "transparent",
                      color: resolutionMode === mode ? "#6366f1" : "#94a3b8",
                      boxShadow:
                        resolutionMode === mode
                          ? "0 1px 4px rgba(0,0,0,0.08)"
                          : "none",
                    }}
                  >
                    {mode === "preset" ? t.presetRatio : t.customResolution}
                  </button>
                ))}
              </div>

              {resolutionMode === "preset" ? (
                /* Ratio Buttons */
                <div
                  className="lite-ratio-selector"
                  style={{ marginBottom: "20px" }}
                >
                  {ratios.map((r) => (
                    <button
                      key={r.value}
                      className={`lite-ratio-btn ${selectedRatio.value === r.value ? "active" : ""}`}
                      onClick={() => setSelectedRatio(r)}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              ) : (
                /* Custom Resolution Inputs */
                <>
                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      marginBottom: "12px",
                      alignItems: "flex-end",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontSize: "11px",
                          color: "#94a3b8",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Width (px)
                      </Text>
                      <Input
                        value={customWidth}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          setCustomWidth(v === "" ? "" : Number(v));
                        }}
                        onBlur={(e) => {
                          const n = Number(e.target.value);
                          setCustomWidth(n > 0 ? n : 1920);
                        }}
                        size="small"
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                    <Text
                      style={{
                        paddingBottom: "6px",
                        color: "#94a3b8",
                        fontWeight: 600,
                      }}
                    >
                      ×
                    </Text>
                    <div style={{ flex: 1 }}>
                      <Text
                        style={{
                          fontSize: "11px",
                          color: "#94a3b8",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Height (px)
                      </Text>
                      <Input
                        value={customHeight}
                        onChange={(e) => {
                          const v = e.target.value.replace(/[^0-9]/g, "");
                          setCustomHeight(v === "" ? "" : Number(v));
                        }}
                        onBlur={(e) => {
                          const n = Number(e.target.value);
                          setCustomHeight(n > 0 ? n : 1080);
                        }}
                        size="small"
                        style={{ borderRadius: "8px" }}
                      />
                    </div>
                  </div>
                  <Button
                    type="primary"
                    icon={<Save size={14} />}
                    loading={isSavingResolution}
                    onClick={handleSaveResolution}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      background: "#6366f1",
                      borderColor: "#6366f1",
                      fontWeight: 600,
                    }}
                  >
                    Save Resolution
                  </Button>
                </>
              )}

              {/* Preview */}
              <div
                ref={previewContainerRef}
                className="lite-screen-preview-container"
                style={{ minHeight: "200px", marginTop: "16px" }}
              >
                <motion.div
                  className="lite-screen-frame"
                  style={{
                    width: previewDims.width,
                    height: previewDims.height,
                  }}
                  layout
                  transition={{ type: "spring", stiffness: 200, damping: 25 }}
                >
                  {viewedPlaylistItems.length > 0 &&
                  viewedPlaylistItems[0].previewUrl ? (
                    viewedPlaylistItems[0].type === "video" ? (
                      <video
                        src={viewedPlaylistItems[0].previewUrl}
                        muted
                        autoPlay
                        loop
                        playsInline
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          opacity: 0.8,
                        }}
                      />
                    ) : (
                      <img
                        src={viewedPlaylistItems[0].previewUrl}
                        alt="Preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          opacity: 0.8,
                        }}
                      />
                    )
                  ) : (
                    <>
                      <MonitorPlay size={28} color="rgba(255,255,255,0.2)" />
                      <Text
                        style={{
                          color: "rgba(255,255,255,0.25)",
                          fontSize: "11px",
                          marginTop: "8px",
                        }}
                      >
                        {t.fullScreenSlot}
                      </Text>
                    </>
                  )}
                  <span className="screen-label">
                    {resolutionMode === "custom"
                      ? `${customWidth}×${customHeight}`
                      : selectedRatio.label}
                  </span>
                </motion.div>
              </div>

              <div style={{ textAlign: "center", marginTop: "12px" }}>
                <Text type="secondary" style={{ fontSize: "12px" }}>
                  {resolutionMode === "custom"
                    ? `${customWidth} × ${customHeight} px • ${t.singleLayout}`
                    : `${selectedRatio.description} (${selectedRatio.label}) • ${t.singleLayout}`}
                </Text>
              </div>

              {/* Action Buttons */}
              <div style={{ display: "flex", gap: "10px", marginTop: "20px" }}>
                <Button
                  icon={<Eye size={14} />}
                  onClick={() => {
                    if (viewedPlaylistItems.length === 0) {
                      message.warning(t.previewAddFirst);
                      return;
                    }
                    setShowPreview(true);
                  }}
                  disabled={viewedPlaylistItems.length === 0}
                  style={{
                    flex: 1,
                    borderRadius: "8px",
                    height: "40px",
                    fontWeight: 600,
                  }}
                >
                  {t.previewBtn}
                </Button>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "4px",
                  }}
                >
                  <Button
                    type="primary"
                    onClick={() => onNavigate("playlist")}
                    style={{
                      width: "100%",
                      borderRadius: "8px",
                      height: "40px",
                      fontWeight: 600,
                      background: "#6366f1",
                      borderColor: "#6366f1",
                    }}
                  >
                    {t.editPlaylist}
                  </Button>
                  <span style={{ fontSize: "12px", color: "#9ca3af" }}>
                    {t.manageContent}
                  </span>
                </div>
              </div>
            </motion.div>
          </Col>
        </Row>
      ) : (
        /* ── No Screen State ── */
        <motion.div
          variants={fadeUp}
          className="lite-empty-state"
          style={{ marginTop: "20px" }}
        >
          <div className="icon-circle">
            <MonitorPlay size={32} color="#6366f1" />
          </div>
          <Title level={3} style={{ marginBottom: "12px", color: "#0f172a" }}>
            {t.noScreenTitle}
          </Title>
          <Text
            type="secondary"
            style={{
              display: "block",
              maxWidth: "420px",
              margin: "0 auto 28px",
              fontSize: "15px",
              lineHeight: "1.6",
            }}
          >
            {t.noScreenText}
          </Text>
          <Button
            type="primary"
            size="large"
            icon={<Link size={16} />}
            onClick={() => setIsPairing(true)}
            style={{
              borderRadius: "8px",
              height: "48px",
              paddingInline: "32px",
              fontWeight: 600,
              background: "#6366f1",
              borderColor: "#6366f1",
            }}
          >
            {t.pairScreen}
          </Button>
        </motion.div>
      )}

      {/* ── Other Screens (Pro) ── */}
      {allScreens.length > 2 && (
        <motion.div variants={fadeUp} style={{ marginTop: "24px" }}>
          <div
            style={{
              padding: "14px 16px",
              borderRadius: "12px",
              background: "#ffffff",
              border: "1px solid #f1f5f9",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "12px",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <div
                style={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "8px",
                  background: "#f8fafc",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Monitor size={16} color="#94a3b8" />
              </div>
              <div>
                <Text strong style={{ fontSize: "13px", color: "#0f172a" }}>
                  Other Screens ({allScreens.length - 2})
                </Text>
                <Text
                  type="secondary"
                  style={{ fontSize: "11px", display: "block" }}
                >
                  Upgrade to Pro to manage all screens
                </Text>
              </div>
            </div>
            <Button
              onClick={onProUpgrade || handleProZones}
              icon={<Crown size={13} color="#222d51" />}
              size="small"
              className="lite-upgrade-btn"
              style={{
                borderRadius: "8px",
                background: "#ffcf5d",
                borderColor: "#ffcf5d",
                color: "#222d51",
                fontWeight: 700,
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
              }}
            >
              Upgrade to{" "}
              <span
                style={{
                  background: "#0f172a",
                  color: "#fff",
                  borderRadius: "4px",
                  padding: "1px 6px",
                  fontSize: "11px",
                  fontWeight: 800,
                  letterSpacing: "0.05em",
                }}
              >
                PRO
              </span>
            </Button>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
              gap: "12px",
            }}
          >
            {allScreens.slice(2).map((s, idx) => (
              <div
                key={s.id || idx}
                style={{
                  padding: "14px 16px",
                  borderRadius: "12px",
                  background: "#f8fafc",
                  border: "1px solid #f1f5f9",
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  opacity: 0.55,
                  cursor: "not-allowed",
                }}
              >
                <div
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "10px",
                    background: "#f1f5f9",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Monitor size={18} color="#94a3b8" />
                </div>
                <div style={{ flex: 1, overflow: "hidden" }}>
                  <Text
                    strong
                    style={{
                      fontSize: "13px",
                      display: "block",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {s.name || s.ScreenName || "Screen"}
                  </Text>
                  <Text type="secondary" style={{ fontSize: "11px" }}>
                    {t.locked}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* ── Pairing Modal ── */}
      <Modal
        title={null}
        open={isPairing}
        onCancel={() => {
          setIsPairing(false);
          setPairCode("");
        }}
        footer={null}
        bodyStyle={{ padding: 0, overflow: "hidden" }}
        width={400}
        centered
      >
        <div
          style={{
            background: "linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)",
            padding: "36px 24px",
            textAlign: "center",
            color: "#fff",
          }}
        >
          <motion.div
            animate={{ y: [0, -8, 0] }}
            transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
          >
            <Smartphone
              size={56}
              color="#ffffff"
              style={{ marginBottom: "14px", opacity: 0.9 }}
            />
          </motion.div>
          <Title level={3} style={{ color: "#fff", margin: 0 }}>
            {t.linkTitle}
          </Title>
          <Text
            style={{
              color: "rgba(255,255,255,0.75)",
              display: "block",
              marginTop: "6px",
              fontSize: "14px",
            }}
          >
            {t.enterCodeHint}
          </Text>
        </div>

        <div style={{ padding: "32px 24px", textAlign: "center" }}>
          <div
            style={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              gap: "8px",
              marginBottom: "28px",
            }}
          >
            <Input
              autoFocus
              ref={inputRef}
              value={pairCode}
              onChange={(e) =>
                setPairCode(e.target.value.replace(/[^A-Za-z0-9]/g, ""))
              }
              maxLength={6}
              style={{
                position: "absolute",
                opacity: 0,
                width: "100%",
                height: "100%",
                cursor: "text",
                zIndex: 10,
              }}
            />
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                style={{
                  width: "44px",
                  height: "54px",
                  borderRadius: "12px",
                  border: `2px solid ${pairCode.length === i ? "#6366f1" : pairCode.length > i ? "#10b981" : "#e2e8f0"}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "22px",
                  fontWeight: 600,
                  color: "#0f172a",
                  background: pairCode.length === i ? "#f5f3ff" : "#ffffff",
                  transition: "all 0.2s ease",
                  boxShadow:
                    pairCode.length === i
                      ? "0 0 0 4px rgba(99,102,241,0.1)"
                      : "none",
                }}
              >
                {pairCode[i] || ""}
              </div>
            ))}
          </div>

          <Button
            type="primary"
            size="large"
            block
            disabled={pairCode.length < 6}
            onClick={handlePair}
            style={{
              height: "48px",
              fontSize: "15px",
              fontWeight: 600,
              borderRadius: "10px",
              background: pairCode.length === 6 ? "#10b981" : "#6366f1",
              borderColor: pairCode.length === 6 ? "#10b981" : "#6366f1",
            }}
          >
            {pairCode.length === 6 ? t.connectNow : t.enterCodePlaceholder}
          </Button>
        </div>
      </Modal>

      {/* ── Preview Player ── */}
      <PreviewPlayer
        visible={showPreview}
        onClose={() => setShowPreview(false)}
        playlistItems={viewedPlaylistItems}
        selectedRatio={
          resolutionMode === "custom"
            ? { value: `${customWidth}/${customHeight}` }
            : selectedRatio
        }
      />
    </motion.div>
  );
};

export default ScreenManager;
